import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Divider, Chip } from '@mui/material';
import { AdminError } from '../../../../types/admin';
import AdminStatusBadge from '../../shared/AdminStatusBadge';

interface ErrorDetailModalProps {
  open: boolean;
  error: AdminError;
  onClose: () => void;
  onResolve?: (errorId: string) => Promise<{ success: boolean }>;
}

export default function ErrorDetailModal({ open, error, onClose, onResolve }: ErrorDetailModalProps) {
  const handleResolve = async () => {
    if (onResolve && !error.resolved) {
      await onResolve(error.id);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Error Details</Typography>
          <AdminStatusBadge status={error.severity} />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Error Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Error Type
          </Typography>
          <Typography variant="body1" gutterBottom>
            {error.error_type}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
            Service
          </Typography>
          <Chip label={error.service} size="small" />

          {error.endpoint && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Endpoint
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                {error.endpoint}
              </Typography>
            </>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Error Message */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Message
          </Typography>
          <Typography variant="body1">{error.message}</Typography>
        </Box>

        {/* Stack Trace */}
        {error.stack_trace && (
          <>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Stack Trace
            </Typography>
            <Box
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                mb: 3,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {error.stack_trace}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Occurrence Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Occurrence Count
          </Typography>
          <Typography variant="h6" color="error">
            {error.occurrence_count}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
            First Occurred
          </Typography>
          <Typography variant="body2">{error.first_occurred_at}</Typography>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
            Last Occurred
          </Typography>
          <Typography variant="body2">{error.last_occurred_at}</Typography>
        </Box>

        {/* Resolution Information */}
        {error.resolved && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Resolution
              </Typography>
              <Chip label="Resolved" color="success" size="small" sx={{ mb: 1 }} />
              {error.resolved_at && (
                <Typography variant="body2" color="text.secondary">
                  Resolved at: {error.resolved_at}
                </Typography>
              )}
              {error.resolved_by && (
                <Typography variant="body2" color="text.secondary">
                  Resolved by: {error.resolved_by}
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!error.resolved && (
          <Button onClick={handleResolve} variant="contained" color="success">
            Mark as Resolved
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
